import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { parseMerchantEvent, matchHeartbeats, applySponsored, sortMerchants, type Merchant } from '@/lib/merchants';
import { BLOCKED_PUBKEYS } from '@/lib/admin';
import { discoveryPool, discoverySignal } from '@/lib/appRelays';
import { useVerifiedMerchants } from './useVerifiedMerchants';
import { useSponsoredMerchants } from './useSponsoredMerchants';
import { useBusinessListings } from './useBusinessListings';
import { useDirectoryListings } from './useDirectoryListings';
import { useBchnostrVerified } from './useBchnostrVerified';
import { useApprovedListings } from './useApprovedListings';

/** Fetch all BCH merchant listings (kind 35341). */
export function useMerchantListings() {
  return useQuery({
    queryKey: ['nostr', 'merchants', 'listings'],
    queryFn: async (c) => {
      const events = await discoveryPool.query(
        [{ kinds: [35341], '#t': ['bch-merchant'], limit: 500 }],
        { signal: discoverySignal(c.signal) },
      );

      const merchants: Merchant[] = [];
      const seen = new Set<string>();

      for (const event of events) {
        // Skip blocked (fake/test) pubkeys
        if (BLOCKED_PUBKEYS.has(event.pubkey)) continue;
        // Deduplicate by pubkey (keep latest)
        if (seen.has(event.pubkey)) continue;
        seen.add(event.pubkey);

        const merchant = parseMerchantEvent(event);
        if (merchant) {
          merchants.push(merchant);
        }
      }

      return merchants;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    placeholderData: keepPreviousData,
    // A refetch that comes back with a temporarily-incomplete relay set should
    // never shrink the list. Merge fresh results with the previous set (union
    // by pubkey) so the count only ever grows toward the complete picture.
    structuralSharing: (prev, next) =>
      unionMerchants(prev as Merchant[] | undefined, next as Merchant[]),
  });
}

/** Fetch heartbeats from the last 7 days (kind 7531). */
export function useHeartbeats() {
  // Bucket `since` to the hour so the query key (and persisted cache) stays
  // stable across renders instead of changing every second.
  const hourBucket = Math.floor(Date.now() / 1000 / 3600);
  const since = hourBucket * 3600 - 7 * 24 * 3600;

  return useQuery({
    queryKey: ['nostr', 'merchants', 'heartbeats', since],
    queryFn: async (c) => {
      const events = await discoveryPool.query(
        [{ kinds: [7531], '#t': ['bch-merchant-activity'], since, limit: 1000 }],
        { signal: discoverySignal(c.signal) },
      );
      return events;
    },
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
}

/**
 * Merge two merchant lists into a union keyed by pubkey, preferring the newest
 * event for any given merchant. Used as TanStack Query's `structuralSharing`
 * so a partial relay response during a refetch never removes merchants that
 * were previously known — the count stays monotonic and stops fluctuating.
 */
function unionMerchants(prev: Merchant[] | undefined, next: Merchant[]): Merchant[] {
  if (!prev || prev.length === 0) return next;

  const byPubkey = new Map<string, Merchant>();
  for (const m of prev) byPubkey.set(m.pubkey, m);
  for (const m of next) {
    const existing = byPubkey.get(m.pubkey);
    // Prefer the newer event when both sets contain the same merchant.
    if (!existing || m.event.created_at >= existing.event.created_at) {
      byPubkey.set(m.pubkey, m);
    }
  }
  return [...byPubkey.values()];
}

/**
 * Combined hook: fetch merchants, heartbeats, verification, and sponsored lists,
 * then enrich, sort, and return the full list.
 */
export function useMerchants() {
  const listings = useMerchantListings();
  const businesses = useBusinessListings();
  const directory = useDirectoryListings();
  const heartbeats = useHeartbeats();
  const verified = useVerifiedMerchants();
  const bchnostrVerified = useBchnostrVerified();
  const approvedListings = useApprovedListings();
  const sponsored = useSponsoredMerchants();

  const isLoading =
    listings.isLoading || businesses.isLoading || directory.isLoading || heartbeats.isLoading;
  const isError = listings.isError && businesses.isError && directory.isError;

  // Merge BCHPAY.Cash listings with BCHNostr business profiles and BCHNostr
  // directory listings. Dedupe by pubkey with a clear precedence (richest data
  // wins for a merchant that appears in more than one source):
  //   1. BCHNostr directory listing  (kind 30023, "Add a listing" form)
  //   2. BCHNostr business profile    (kind 30499, professional profile)
  //   3. BCHPAY.Cash listing          (kind 35341)
  // Later writes override earlier ones, so BCHPAY takes top priority and the
  // directory listing is only used when a merchant has nothing richer.
  let merchants: Merchant[] = [];
  {
    const approved = new Set(approvedListings.data ?? []);
    const byPubkey = new Map<string, Merchant>();
    for (const m of directory.data ?? []) {
      byPubkey.set(m.pubkey, m);
    }
    for (const m of businesses.data ?? []) {
      // Direct BCH Local listings only appear once the admin has approved them.
      if (m.source === 'bch-local' && !approved.has(m.pubkey)) continue;
      byPubkey.set(m.pubkey, m);
    }
    for (const m of listings.data ?? []) {
      byPubkey.set(m.pubkey, m);
    }
    merchants = [...byPubkey.values()];
  }

  // Apply heartbeats to the merged list
  if (heartbeats.data) {
    merchants = matchHeartbeats(merchants, heartbeats.data);
  }

  // Apply verification status. A merchant is verified if ANY trusted source
  // says so:
  //  - the BCHPAY admin verified list (kind 30078 p-tags), or
  //  - an active BCHNostr verification grant (kind 30078 from a trusted issuer).
  if (verified.data || bchnostrVerified.data) {
    const adminVerified = new Set(verified.data ?? []);
    const grantVerified = new Set(bchnostrVerified.data ?? []);
    merchants = merchants.map(m => ({
      ...m,
      verified: m.verified || adminVerified.has(m.pubkey) || grantVerified.has(m.pubkey),
    }));
  }

  // Apply sponsored status
  if (sponsored.data) {
    merchants = applySponsored(merchants, new Set(sponsored.data));
  }

  // Sort: sponsored first, then verified, then rest
  merchants = sortMerchants(merchants);

  return {
    merchants,
    isLoading,
    isError,
    refetch: () => {
      listings.refetch();
      businesses.refetch();
      directory.refetch();
      heartbeats.refetch();
      verified.refetch();
      bchnostrVerified.refetch();
      approvedListings.refetch();
      sponsored.refetch();
    },
  };
}
